import { useEffect, useMemo, useState } from 'react';
import { connectors } from '@/connectors';
import { useWeb3React } from '@web3-react/core';
import {
  useLazyWalletControllerGetMetamaskLoginMessageQuery,
  useWalletControllerAuthenticateMutation,
  useWalletControllerConnectWalletMutation,
} from '@/store/services/MidleNestApi.service';
import { useModal, useProfile } from '@/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import metamaskSvg from '@/assets/images/metamask.svg';
import walletConnectSvg from '@/assets/images/wallet-connect.svg';
import { useAuthenticated } from '@/hooks/useAuthenticated.hook';
import { useDispatch } from 'react-redux';
import CustomError from '@/types/CustomError.class';
import { storageLocal } from '@/utils';
import useKeplrConnect from './useKeplrConnect';
import useLeapConnect from './useLeapConnect';

export default function Connections() {
  const { library, account, activate, deactivate } = useWeb3React();
  const handleKeeplrConnect = useKeplrConnect();
  const handleLeapConnect = useLeapConnect();
  const [isConnecting, setConnecting] = useState(false);

  const [getMessage] = useLazyWalletControllerGetMetamaskLoginMessageQuery();
  const [walletLogin] = useWalletControllerAuthenticateMutation();
  const [walletConnect] = useWalletControllerConnectWalletMutation();

  const dispatch = useDispatch();
  const { reloadProfile } = useProfile();
  const { profile } = useProfile();
  const { isAuthenticated } = useAuthenticated();
  const { hideModal } = useModal();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const connections = useMemo(
    () => ['Twitter', 'Google', 'Telegram', 'Discord', 'Github', 'Steam'],
    [],
  );

  // TODO: 'wallet' yerine enum kullanilmali
  const wallet = profile?.user?.accounts?.filter((x) => x.provider == 'wallet')[0];

  const handleSignMessage = async (message?: string | null): Promise<string> => {
    if (!library || !account || !message) return '';
    try {
      const signer = await library.provider.getSigner(account);
      const signature = (await signer.signMessage(message)) as string;
      return signature;
    } catch (error) {
      console.log({ error });
      return '';
    }
  };

  useEffect(() => {
    // console.log({ account });
    if (account && !isConnecting) {
      setConnecting(true);
      getMessage(account)
        .unwrap()
        .then((payload) => {
          const message = payload?.message;
          handleSignMessage(message).then((signature) => {
            if (isAuthenticated) {
              walletConnect({ accountAddress: account, message, signature })
                .unwrap()
                .then((res) => {
                  gtag('event', 'connect-wallet');
                  reloadProfile();
                  hideModal();
                  //deactivate();
                  //return navigate('/', {});
                })
                .catch((e) => {
                  console.log({ e });
                });
            } else {
              const ref = storageLocal.getItem('ref') || undefined;
              walletLogin({ accountAddress: account, message, signature, ref })
                .unwrap()
                .then((res) => {
                  gtag('event', 'auth-wallet');
                  reloadProfile();
                  hideModal();
                  deactivate();
                  return navigate('/', {});
                })
                .catch((e: CustomError) => {
                  console.log(e);
                  // TODO  display the error message
                  // dispatch(systemActions.pushMessage({ message: e.message }));
                });
            }
          });
        });
    }
  }, [account, isConnecting]);

  const handleMetamask = async () => {
    // if (status === 'unavailable') alert(t('Please, install metamask extension first!'));
    setConnecting(false);
    // TODO check if error
    await activate(connectors('metamask'));
  };
  const handleWalletConnect = async () => {
    setConnecting(false);
    await activate(connectors('walletConnect'), (err) => {
      console.error(err);
    });
  };

  // const handleCoinbase = async () => {
  //   await activate(connectors('coinbaseWallet'), (err) => {
  //     console.error(err);
  //   });
  //   setProvider('coinbaseWallet');
  // };

  return (
    <div>
      {/* // TODO wallet'in connect olmasi login olmami saglamiyor???*/}
      {/*{wallet ? (*/}
      {/*  <div className=''>{t('Your wallet is connected!')}</div>*/}
      {/*) : (*/}
      <div className='min-w-[300px] grid grid-cols-2 gap-3 font-semibold'>
        <button
          type='button'
          onClick={handleMetamask}
          className='btn bg-white dark:bg-[#FFDA68] dark:border-transparent dark:hover:bg-[#FFDA68] dark:text-[#151515] dark:hover:text-[#151515] dark:hover:brightness-75 !p-2'
        >
          <Trans
            i18nKey='<i/> Metamask'
            components={{
              i: <img src={metamaskSvg} className='h-6 mr-2 inline-block' alt='metamask' />,
            }}
          />
        </button>
        <button
          type='button'
          onClick={handleWalletConnect}
          className='btn bg-white dark:bg-[#FFDA68] dark:border-transparent dark:hover:bg-[#FFDA68] dark:text-[#151515] dark:hover:text-[#151515] dark:hover:brightness-75 !p-2'
        >
          <Trans
            i18nKey='<i/> Wallet Connect'
            components={{
              i: (
                <img
                  src={walletConnectSvg}
                  className='h-6 mr-2 inline-block'
                  alt='wallet connect'
                />
              ),
            }}
          />
        </button>
        <button
          type='button'
          onClick={handleKeeplrConnect}
          className='btn bg-white dark:bg-[#FFDA68] dark:border-transparent dark:hover:bg-[#FFDA68] dark:text-[#151515] dark:hover:text-[#151515] dark:hover:brightness-75 !p-2'
        >
          <Trans
            i18nKey='<i/> Keplr'
            components={{
              i: <img src='/keplr-k.png' className='h-6 mr-2 inline-block' alt='wallet connect' />,
            }}
          />
        </button>
        <button
          type='button'
          onClick={handleLeapConnect}
          className='btn bg-white dark:bg-[#FFDA68] dark:border-transparent dark:hover:bg-[#FFDA68] dark:text-[#151515] dark:hover:text-[#151515] dark:hover:brightness-75 !p-2'
        >
          <Trans
            i18nKey='<i/> Leap Cosmos'
            components={{
              i: (
                <img
                  src='/leap-cosmos-logo.png'
                  className='h-6 mr-2 inline-block'
                  alt='leap cosmos'
                />
              ),
            }}
          />
        </button>
      </div>
      {/*)}*/}
      <div className='flex items-center justify-center gap-2 md:gap-4 mt-4'>
        {connections.map((connection, index) => (
          <Link
            key={index}
            to={`/connect/${connection.toLowerCase()}`}
            className='rounded-full p-4 hover:bg-blue-50 dark:bg-[#FFDA68]/10 dark:hover:bg-[#FFDA68] hover:text-blue-500 dark:hover:text-[#151515] dark:text-[#FFDA68]'
            title={t('Connect with {{connection}}', { connection })}
          >
            {/* Connection's first letter is capital, rest is same */}

            <i className={`icon-${connection.toLowerCase()}`} />
          </Link>
        ))}
        {/*<Link*/}
        {/*  to={'/connect/reddit'}*/}
        {/*  className='rounded-full p-4 hover:bg-blue-50 hover:text-blue-500'*/}
        {/*  title={t('Connect with Reddit')}*/}
        {/*>*/}
        {/*  <i className='icon-reddit' />*/}
        {/*</Link>*/}
      </div>
    </div>
  );
}
